import { useLocation } from 'react-router-dom'
import { Screen } from '../ui/Screen.jsx'

/**
 * The ending, for a participant who has left the study.
 *
 * IT MUST RENDER WITH NO SESSION AND NO API CALL. After withdrawal every other
 * endpoint returns 401 and login returns 403, so anything that fetched would
 * bounce the participant to a sign-in form — which is the opposite of an
 * ending. Everything here is either passed in from the withdrawal response or
 * static.
 *
 * It says three things and stops: the check-ins have stopped, the answers
 * already given are kept and still used, and thank you. It does not offer to
 * undo, does not offer deletion, and does not ask them to do anything.
 *
 * NOTE FOR design: backend is adding a `completed` state for participants who
 * finish all 30 beeps. That ending and this one are the same moment from the
 * participant's side — the study is over for them — and probably want one
 * shape. This is deliberately plain rather than designed, so it can be replaced
 * wholesale rather than argued with.
 */
export default function Withdrawn() {
  const { state } = useLocation()

  /*
   * CR-14 FINDING 4 — NO STRING IS EVER CONDITIONED ON THE CONTENT OF ANOTHER
   * STRING.
   *
   * This screen used to decide whether to state that her data is kept by
   * testing `detail?.includes('kept')` — suppressing its own retention
   * paragraph because the server's English sentence happened to contain four
   * particular characters. That is a cross-seam coupling through prose, on the
   * last screen a withdrawn participant can ever reach.
   *
   * Rewording the server's sentence to "we keep the answers you already gave"
   * — still true, still reviewed — would have told her twice. Rewording it to
   * anything containing "kept" but not the retention claim would have deleted
   * the fact from the primary path. Either way the browser walk stayed green,
   * because it asserts /kept/i only on the RELOAD branch, where `detail` is
   * absent and the client's own paragraph renders. The one place the fact was
   * checked was the one path where it could not fail.
   *
   * THE FIX IS NOT A BETTER MATCH. Matching at all was the defect. The client
   * owns this fact outright and states it from a BOOLEAN the server has always
   * sent and this screen never read.
   */
  const dataRetained = state?.dataRetained
  const copy = state?.copy

  return (
    <Screen disposition="status">
      <div className="py-6">
        <h1 className="t-title text-[var(--ink-900)]">Editrics has ended for you</h1>

        <p className="mt-4 text-base leading-relaxed text-[var(--ink-700)]">
          You have stopped Editrics and will receive no further check-ins.
        </p>

        <p className="mt-3 text-base leading-relaxed text-[var(--ink-700)]">
          You won&rsquo;t hear from us again - no more reminders.
        </p>

        {/*
          Retention is a property of the ACT, not of the response: withdrawal
          means stop and explicitly retains what was collected, which is what
          makes it a different act from erasure. So the sentence is suppressed
          only if the server states outright that data was NOT retained — a
          contract change we would want to see, rather than one that could
          arrive silently through a reworded sentence.

          Absent (a reload, or a sign-in weeks later) renders it, because
          someone arriving then should not have to remember what a confirmation
          dialog once told her about her own data.
        */}
        {dataRetained === false ? null : (
          <p className="mt-3 text-base leading-relaxed text-[var(--ink-700)]">
            {copy?.retention ??
              'The answers you have already given are kept, and are used in the research.'}
          </p>
        )}

        {copy?.erasure_route ? (
          <p className="mt-3 text-base leading-relaxed text-[var(--ink-700)]">
            {copy.erasure_route}
          </p>
        ) : null}

        <p className="mt-6 text-base leading-relaxed text-[var(--ink-700)]">
          Thank you for the time you gave this. It was genuinely useful.
        </p>
      </div>
    </Screen>
  )
}
